import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import api from "../Utils/api";
import { format, startOfWeek, endOfWeek, eachDayOfInterval, parseISO } from "date-fns";
import Spinner from "../Components/Spinner";
import { io } from "socket.io-client";
import DateRangePicker from "../Components/Timesheet/DateRangePicker";
import { formatIrelandTime } from "../Utils/timezoneHelper";
import Select from "react-select";
import * as XLSX from "xlsx";

export default function WorkTimeHome() {
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Store all users for filtering
  const [schedules, setSchedules] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 1 });
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return endOfWeek(today, { weekStartsOn: 1 });
  });

  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [isStaffDropdownOpen, setIsStaffDropdownOpen] = useState(false);
  const [staffSearchTerm, setStaffSearchTerm] = useState("");
  const [dateError, setDateError] = useState("");
  const [realTimeStatus, setRealTimeStatus] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date()); // For real-time updates
  const socketRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user) {
      getUsers();
      if (user.deputyAccessLevel === "system_administrator") {
        fetchCompanies();
      }
    }
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get("/company");
      if (response.data.success) {
        // Only show active (non-archived) companies
        setCompanies((response.data.companies || []).filter(company => !company.archive));
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  // Initialize Socket.IO connection
  useEffect(() => {
    // Initialize socket connection - use the same URL as your API
    const socketUrl = process.env.REACT_APP_SERVER_URL || 
      process.env.REACT_APP_SERVER_ADDRESS?.replace('/api', '') || 
      "http://localhost:8001";
    const socket = io(socketUrl);
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Connected to server via Socket.IO");
    });

    socket.on("scheduleUpdate", (data) => {
      // Update schedules when changes occur
      console.log("Schedule update received:", data);
      getSchedules();
    });
    
    // Also listen for timesheet updates
    socket.on("timesheetUpdate", (data) => {
      console.log("Timesheet update received:", data);
      getSchedules();
    });

    socket.on("clockIn", (data) => {
      console.log("Clock in:", data);
      // Refresh schedules to get updated clock times
      getSchedules();
      updateRealTimeStatus();
    });

    socket.on("clockOut", (data) => {
      console.log("Clock out:", data);
      // Refresh schedules to get updated clock times
      getSchedules();
      updateRealTimeStatus();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Update real-time status
  const updateRealTimeStatus = () => {
    const statusMap = {};
    const now = new Date();

    users.forEach((user) => {
      // Find today's active shifts from schedules
      const todayStart = new Date(now);
      todayStart.setUTCHours(0, 0, 0, 0);
      const todayEnd = new Date(now);
      todayEnd.setUTCHours(23, 59, 59, 999);

      const userSchedules = schedules.filter((s) => {
        if (s.userID?._id !== user._id) return false;
        if (!s.startTime) return false;
        const schedStart = new Date(s.startTime);
        const schedEnd = new Date(s.endTime || s.startTime);
        return schedStart <= todayEnd && schedEnd >= todayStart;
      });

      // Also check timesheets for active clock-in
      const activeTimesheet = timesheets.find((ts) => {
        if (ts.userID?._id !== user._id) return false;
        return ts.startTime && !ts.endTime; // Clocked in but not clocked out
      });

      // Find active shift (clocked in but not clocked out) from schedules
      const activeShift = userSchedules.find(
        (s) => s.clockIn && !s.clockOut
      );

      // Use timesheet if no schedule found, or use schedule if available
      if (activeShift || activeTimesheet) {
        const clockInTime = activeShift?.clockIn 
          ? new Date(activeShift.clockIn)
          : (activeTimesheet?.startTime ? new Date(activeTimesheet.startTime) : null);
        
        statusMap[user._id] = {
          status: "Working",
          lastUpdate: clockInTime,
          clockInTime: activeShift?.clockIn || activeTimesheet?.startTime,
        };
      } else {
        statusMap[user._id] = { status: "Clocked Out", lastUpdate: null };
      }
    });

    setRealTimeStatus(statusMap);
  };

  useEffect(() => {
    if (users.length > 0 && (schedules.length > 0 || timesheets.length > 0)) {
      updateRealTimeStatus();
    }
  }, [users, schedules, timesheets]);

  useEffect(() => {
    // Validate dates
    if (!startDate || !endDate) {
      setDateError("Both start and end dates are required");
      return;
    }
    
    if (endDate <= startDate) {
      setDateError("End date must be greater than start date");
      return;
    }
    
    setDateError("");
    getSchedules();
    generateDateRange();
  }, [startDate, endDate]);

  useEffect(() => {
    filterSchedules();
  }, [schedules, selectedUsers]);

  // Update current time every minute for real-time duration calculations on today's active shifts
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStaffDropdownOpen(false);
      }
    };

    if (isStaffDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isStaffDropdownOpen]);

  const getUsers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/user");
      setIsLoading(false);
      // Only show employees, and ensure we have automatic break settings
      const employeeUsers = response.data.users.filter(
        (u) => !u.archive && (u.deputyAccessLevel === "employee" || u.deputyAccessLevel === "company_manager")
      );
      setAllUsers(employeeUsers); // Store all users
      // Apply company filter if selected
      let filteredUsers = employeeUsers;
      if (user && user.deputyAccessLevel === "system_administrator" && selectedCompany) {
        filteredUsers = employeeUsers.filter((u) => {
          // Handle both populated and non-populated companyID
          const userCompanyID = u.companyID?._id || u.companyID;
          return userCompanyID && userCompanyID.toString() === selectedCompany._id.toString();
        });
      }
      setUsers(filteredUsers);
      setSelectedUsers(filteredUsers.map((u) => u._id)); // Select all by default
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch users");
    }
  };

  // Re-filter users when company selection changes
  useEffect(() => {
    if (user && user.deputyAccessLevel === "system_administrator" && allUsers.length > 0) {
      let filteredUsers = allUsers;
      if (selectedCompany) {
        filteredUsers = allUsers.filter((u) => {
          // Handle both populated and non-populated companyID
          const userCompanyID = u.companyID?._id || u.companyID;
          return userCompanyID && userCompanyID.toString() === selectedCompany._id.toString();
        });
      }
      setUsers(filteredUsers);
      setSelectedUsers(filteredUsers.map((u) => u._id)); // Select all filtered users
    }
  }, [selectedCompany, allUsers, user]);

  const getSchedules = async () => {
    try {
      const start = new Date(startDate);
      start.setUTCHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setUTCHours(23, 59, 59, 999);

      // Fetch both schedules and timesheets
      const [scheduleResponse, timesheetResponse] = await Promise.all([
        api.post("/schedule/list", {
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          admin: true,
        }),
        api.post("/timesheet/list", {
          startTime: start.toISOString(),
          endTime: end.toISOString(),
        }).catch(() => ({ data: { success: false, timesheets: [] } })) // Handle error gracefully
      ]);

      setSchedules(scheduleResponse.data.schedules || []);
      setTimesheets(timesheetResponse.data.timesheets || []);
      // Refresh schedules on clock events
      updateRealTimeStatus();
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch schedules");
    }
  };

  const generateDateRange = () => {
    const dates = eachDayOfInterval({ start: startDate, end: endDate });
    setDateRange(dates);
  };

  const filterSchedules = () => {
    const filtered = schedules.filter((schedule) =>
      selectedUsers.includes(schedule.userID?._id)
    );
    setFilteredSchedules(filtered);
  };

  const formatTime = (dateString) => {
    return formatIrelandTime(dateString, 'time');
  };

  const formatDate = (date) => {
    return format(date, "EEE dd");
  };

  // Get all merged schedules and timesheets for a user (for summary calculations)
  const getMergedSchedulesForUser = (userID) => {
    const userSchedules = filteredSchedules.filter(
      (s) => s.userID?._id === userID
    );
    
    // Also get timesheets for this user within the date range
    const start = new Date(startDate);
    start.setUTCHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setUTCHours(23, 59, 59, 999);
    
    const userTimesheets = timesheets.filter((timesheet) => {
      if (timesheet.userID?._id !== userID) return false;
      if (!timesheet.startTime) return false;
      const tsStart = new Date(timesheet.startTime);
      return tsStart >= start && tsStart <= end;
    });

    // Merge timesheet data into schedules (same logic as getSchedulesForDay)
    const mergedSchedules = [...userSchedules];
    const matchedTimesheetIds = new Set(); // Track which timesheets have been matched

    userTimesheets.forEach((timesheet) => {
      // First, try to match by timesheetID if schedule has one
      let matchingSchedule = userSchedules.find((sched) => {
        if (sched.timesheetID) {
          const schedTimesheetId = sched.timesheetID?._id || sched.timesheetID;
          const tsId = timesheet._id;
          return schedTimesheetId && schedTimesheetId.toString() === tsId.toString();
        }
        return false;
      });

      // If no match by timesheetID, try to find matching schedule by location and time proximity
      if (!matchingSchedule) {
        matchingSchedule = userSchedules.find((sched) => {
          // Skip if this schedule already has clock times from another timesheet
          // (to avoid matching multiple timesheets to the same schedule)
          if (sched.clockIn) {
            // Check if this schedule's clockIn matches a different timesheet
            const schedClockIn = new Date(sched.clockIn);
            const tsStart = new Date(timesheet.startTime);
            const timeDiff = Math.abs(schedClockIn - tsStart);
            // If clock times are more than 5 minutes apart, they're different shifts
            if (timeDiff > 5 * 60 * 1000) {
              return false;
            }
          }

          // Match by location if available
          const sameLocation = sched.locationID?._id === timesheet.locationID?._id ||
                             sched.locationID?._id === timesheet.clockInLocationID?._id ||
                             (!sched.locationID && (timesheet.locationID || timesheet.clockInLocationID));
          
          // Match by time (within 4 hours of schedule start)
          if (sched.startTime) {
            const schedStart = new Date(sched.startTime);
            const tsStart = new Date(timesheet.startTime);
            const timeDiff = Math.abs(schedStart - tsStart);
            const hoursDiff = timeDiff / (1000 * 60 * 60);
            
            return sameLocation && hoursDiff < 4;
          }
          return sameLocation;
        });
      }

      if (matchingSchedule) {
        // Update existing schedule with timesheet clock times
        if (!matchingSchedule.clockIn && timesheet.startTime) {
          matchingSchedule.clockIn = timesheet.startTime;
        }
        if (!matchingSchedule.clockOut && timesheet.endTime) {
          matchingSchedule.clockOut = timesheet.endTime;
        }
        if (timesheet.locationID && !matchingSchedule.locationID) {
          matchingSchedule.locationID = timesheet.locationID;
        }
        // Mark this timesheet as matched
        matchedTimesheetIds.add(timesheet._id.toString());
      }
    });

    // Only create virtual schedules for timesheets that weren't matched to any schedule
    userTimesheets.forEach((timesheet) => {
      if (!matchedTimesheetIds.has(timesheet._id.toString())) {
        // Create virtual schedule entry from timesheet
        const virtualSchedule = {
          _id: timesheet._id,
          userID: timesheet.userID,
          locationID: timesheet.locationID || timesheet.clockInLocationID,
          startTime: timesheet.startTime,
          endTime: timesheet.endTime || timesheet.startTime,
          clockIn: timesheet.startTime,
          clockOut: timesheet.endTime || null,
          breakStartTime: timesheet.breakStartTime,
          breakEndTime: timesheet.breakEndTime,
          type: "shift",
          status: timesheet.endTime ? "pending" : "started",
          isVirtual: true,
        };
        mergedSchedules.push(virtualSchedule);
      }
    });

    // Deduplicate: Remove any schedules that are duplicates
    // For schedules with clock-in: deduplicate by userID + clockIn time
    // For schedules without clock-in: deduplicate by userID + startTime + endTime + locationID
    // Use normalized times to handle millisecond differences
    const normalizeTimeForDedup = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      // Round to nearest minute to handle millisecond differences
      date.setSeconds(0, 0);
      return date.toISOString();
    };
    
    const getLocationIdForDedup = (schedule) => {
      if (!schedule.locationID) return 'no_location';
      // Handle both populated and non-populated locationID
      if (typeof schedule.locationID === 'object' && schedule.locationID._id) {
        return schedule.locationID._id.toString();
      }
      if (typeof schedule.locationID === 'string') {
        return schedule.locationID;
      }
      return schedule.locationID.toString();
    };
    
    const seen = new Set();
    const deduplicated = mergedSchedules.filter((schedule) => {
      const clockIn = schedule.clockIn || (schedule.isVirtual && schedule.startTime);
      
      if (clockIn) {
        // For schedules with clock-in, use normalized clock-in time to identify duplicates
        const clockInTime = normalizeTimeForDedup(clockIn);
        const key = `${userID}_clockIn_${clockInTime}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      } else {
        // For schedules without clock-in, use normalized startTime + endTime + locationID to identify duplicates
        // This catches duplicate schedule records that haven't been clocked in yet
        if (!schedule.startTime || !schedule.endTime) {
          // Fallback to ID if no times available
          const key = `${userID}_noClock_${schedule._id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }
        
        const startTime = normalizeTimeForDedup(schedule.startTime);
        const endTime = normalizeTimeForDedup(schedule.endTime);
        const locationId = getLocationIdForDedup(schedule);
        const key = `${userID}_scheduled_${startTime}_${endTime}_${locationId}`;
        
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }
    });

    return deduplicated;
  };

  // Calculate total hours from actual clock times (clockIn/clockOut) ONLY
  // Excludes scheduled hours that don't have actual clock-in times
  const calculateTotalHours = (schedules) => {
    let totalMilliseconds = 0;
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setUTCHours(0, 0, 0, 0);
    
    schedules.forEach((schedule) => {
      // ONLY use actual clock times - ignore scheduled times
      // For virtual schedules (from timesheets), use startTime/endTime as they are actual clock times
      const start = schedule.clockIn ? new Date(schedule.clockIn) : 
                    (schedule.isVirtual && schedule.startTime ? new Date(schedule.startTime) : null);
      
      // Only calculate if we have actual clock-in time (not just scheduled)
      if (!start) return; // Skip entries without actual clock-in
      
      // Determine end time:
      // 1. If clockOut exists, use it
      // 2. If no clockOut but it's today and shift is active, use current time (live calculation)
      // 3. If no clockOut and it's a past date, skip this schedule (don't count incomplete shifts)
      let actualEnd = null;
      if (schedule.clockOut) {
        actualEnd = new Date(schedule.clockOut);
      } else if (schedule.isVirtual && schedule.endTime) {
        // For virtual schedules (timesheets), use endTime if available
        actualEnd = new Date(schedule.endTime);
      } else if (schedule.clockIn) {
        // Check if this shift started today
        const startDate = new Date(start);
        startDate.setUTCHours(0, 0, 0, 0);
        const isToday = startDate.getTime() === todayStart.getTime();
        
        if (isToday) {
          // Only use current time for today's active shifts
          actualEnd = now;
        }
        // If it's a past date and no clockOut, actualEnd remains null (will skip)
      }
      
      // Skip if no end time (incomplete shifts from past dates)
      if (!actualEnd) return;
      
      if (start && actualEnd) {
        let duration = actualEnd - start;

        // Subtract actual break time if exists
        if (schedule.breakClockIn && schedule.breakClockOut) {
          const breakStart = new Date(schedule.breakClockIn);
          const breakEnd = new Date(schedule.breakClockOut);
          const breakDuration = breakEnd - breakStart;
          if (breakDuration > 0 && breakDuration < duration) {
            duration -= breakDuration;
          }
        } else if (schedule.breakStartTime && schedule.breakEndTime && schedule.isVirtual) {
          // Only use scheduled break time for virtual schedules (timesheets)
          const breakStart = new Date(schedule.breakStartTime);
          const breakEnd = new Date(schedule.breakEndTime);
          const breakDuration = breakEnd - breakStart;
          if (breakDuration > 0 && breakDuration < duration) {
            duration -= breakDuration;
          }
        }
        
        totalMilliseconds += duration;
      }
    });
    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const getSchedulesForDay = (userID, date) => {
    const dayStart = new Date(date);
    dayStart.setUTCHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setUTCHours(23, 59, 59, 999);

    // Get schedules for this day - only show schedules that START on this date
    // This prevents overnight shifts from appearing on both start and end dates
    let daySchedules = filteredSchedules.filter((schedule) => {
      if (schedule.userID?._id !== userID) return false;
      if (!schedule.startTime) return false;
      
      // Get the date string (YYYY-MM-DD) of the schedule's start time
      const schedStart = new Date(schedule.startTime);
      const schedStartDate = new Date(schedStart);
      schedStartDate.setUTCHours(0, 0, 0, 0);
      
      // Get the date string (YYYY-MM-DD) of the target date
      const targetDate = new Date(date);
      targetDate.setUTCHours(0, 0, 0, 0);
      
      // Only include schedules where the start date matches the target date
      return schedStartDate.getTime() === targetDate.getTime();
    });

    // Initial deduplication of schedules: Remove exact duplicates (same startTime, endTime, locationID)
    // This prevents showing multiple schedule records for the exact same shift
    // Normalize times to minutes to handle millisecond differences
    const normalizeTime = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      // Round to nearest minute to handle millisecond differences
      date.setSeconds(0, 0);
      return date.toISOString();
    };
    
    const getLocationId = (schedule) => {
      if (!schedule.locationID) return 'no_location';
      // Handle both populated and non-populated locationID
      if (typeof schedule.locationID === 'object' && schedule.locationID._id) {
        return schedule.locationID._id.toString();
      }
      if (typeof schedule.locationID === 'string') {
        return schedule.locationID;
      }
      return schedule.locationID.toString();
    };
    
    const scheduleMap = new Map();
    
    daySchedules.forEach((schedule) => {
      if (!schedule.startTime || !schedule.endTime) {
        // For schedules without times, use ID to identify
        const key = `${userID}_${schedule._id}`;
        if (!scheduleMap.has(key)) {
          scheduleMap.set(key, schedule);
        }
        return;
      }
      
      // Normalize times to minutes to handle millisecond differences
      const startTime = normalizeTime(schedule.startTime);
      const endTime = normalizeTime(schedule.endTime);
      const locationId = getLocationId(schedule);
      const key = `${userID}_${startTime}_${endTime}_${locationId}`;
      
      // If we already have a schedule with this exact start time, end time, and location
      if (scheduleMap.has(key)) {
        const existing = scheduleMap.get(key);
        // Prefer schedule with clock-in time, or if both/none have clock-in, keep the first one
        if (schedule.clockIn && !existing.clockIn) {
          scheduleMap.set(key, schedule); // Replace with one that has clock-in
        }
        // Otherwise keep the existing one (first one found)
      } else {
        scheduleMap.set(key, schedule);
      }
    });
    
    // Convert map back to array
    daySchedules = Array.from(scheduleMap.values());

    // Also check timesheets for this day and merge with schedules
    const dayTimesheets = timesheets.filter((timesheet) => {
      if (timesheet.userID?._id !== userID) return false;
      if (!timesheet.startTime) return false;
      const tsStart = new Date(timesheet.startTime);
      return tsStart >= dayStart && tsStart <= dayEnd;
    });

    // Merge timesheet data into schedules
    // If there's a schedule for this day, update it with timesheet clock times
    // If there's no schedule but there's a timesheet, create a virtual schedule entry
    const mergedSchedules = [...daySchedules];
    const matchedTimesheetIds = new Set(); // Track which timesheets have been matched

    dayTimesheets.forEach((timesheet) => {
      // First, try to match by timesheetID if schedule has one
      let matchingSchedule = daySchedules.find((sched) => {
        if (sched.timesheetID) {
          const schedTimesheetId = sched.timesheetID?._id || sched.timesheetID;
          const tsId = timesheet._id;
          return schedTimesheetId && schedTimesheetId.toString() === tsId.toString();
        }
        return false;
      });

      // If no match by timesheetID, try to find matching schedule by location and time proximity
      if (!matchingSchedule) {
        matchingSchedule = daySchedules.find((sched) => {
          // Skip if this schedule already has clock times from another timesheet
          // (to avoid matching multiple timesheets to the same schedule)
          if (sched.clockIn) {
            // Check if this schedule's clockIn matches a different timesheet
            const schedClockIn = new Date(sched.clockIn);
            const tsStart = new Date(timesheet.startTime);
            const timeDiff = Math.abs(schedClockIn - tsStart);
            // If clock times are more than 5 minutes apart, they're different shifts
            if (timeDiff > 5 * 60 * 1000) {
              return false;
            }
          }

          // Match by location if available
          const sameLocation = sched.locationID?._id === timesheet.locationID?._id ||
                             sched.locationID?._id === timesheet.clockInLocationID?._id ||
                             (!sched.locationID && (timesheet.locationID || timesheet.clockInLocationID));
          
          // Match by time (within 4 hours of schedule start)
          if (sched.startTime) {
            const schedStart = new Date(sched.startTime);
            const tsStart = new Date(timesheet.startTime);
            const timeDiff = Math.abs(schedStart - tsStart);
            const hoursDiff = timeDiff / (1000 * 60 * 60);
            
            return sameLocation && hoursDiff < 4;
          }
          return sameLocation;
        });
      }

      if (matchingSchedule) {
        // Update existing schedule with timesheet clock times
        if (!matchingSchedule.clockIn && timesheet.startTime) {
          matchingSchedule.clockIn = timesheet.startTime;
        }
        if (!matchingSchedule.clockOut && timesheet.endTime) {
          matchingSchedule.clockOut = timesheet.endTime;
        }
        if (timesheet.locationID && !matchingSchedule.locationID) {
          matchingSchedule.locationID = timesheet.locationID;
        }
        // Mark this timesheet as matched
        matchedTimesheetIds.add(timesheet._id.toString());
      }
    });

    // Only create virtual schedules for timesheets that weren't matched to any schedule
    dayTimesheets.forEach((timesheet) => {
      if (!matchedTimesheetIds.has(timesheet._id.toString())) {
        // Create virtual schedule entry from timesheet
        const virtualSchedule = {
          _id: timesheet._id,
          userID: timesheet.userID,
          locationID: timesheet.locationID || timesheet.clockInLocationID,
          startTime: timesheet.startTime,
          endTime: timesheet.endTime || timesheet.startTime, // Use startTime as fallback
          clockIn: timesheet.startTime,
          clockOut: timesheet.endTime || null,
          breakStartTime: timesheet.breakStartTime,
          breakEndTime: timesheet.breakEndTime,
          type: "shift",
          status: timesheet.endTime ? "pending" : "started",
          isVirtual: true, // Mark as virtual so we know it's from timesheet
        };
        mergedSchedules.push(virtualSchedule);
      }
    });

    // Deduplicate: Remove any schedules that are duplicates
    // For schedules with clock-in: deduplicate by userID + clockIn time
    // For schedules without clock-in: deduplicate by userID + startTime + endTime + locationID
    // Use normalized times to handle millisecond differences
    const normalizeTimeForDedup = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      // Round to nearest minute to handle millisecond differences
      date.setSeconds(0, 0);
      return date.toISOString();
    };
    
    const getLocationIdForDedup = (schedule) => {
      if (!schedule.locationID) return 'no_location';
      // Handle both populated and non-populated locationID
      if (typeof schedule.locationID === 'object' && schedule.locationID._id) {
        return schedule.locationID._id.toString();
      }
      if (typeof schedule.locationID === 'string') {
        return schedule.locationID;
      }
      return schedule.locationID.toString();
    };
    
    const seen = new Set();
    const deduplicated = mergedSchedules.filter((schedule) => {
      const clockIn = schedule.clockIn || (schedule.isVirtual && schedule.startTime);
      
      if (clockIn) {
        // For schedules with clock-in, use normalized clock-in time to identify duplicates
        const clockInTime = normalizeTimeForDedup(clockIn);
        const key = `${userID}_clockIn_${clockInTime}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      } else {
        // For schedules without clock-in, use normalized startTime + endTime + locationID to identify duplicates
        // This catches duplicate schedule records that haven't been clocked in yet
        if (!schedule.startTime || !schedule.endTime) {
          // Fallback to ID if no times available
          const key = `${userID}_noClock_${schedule._id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }
        
        const startTime = normalizeTimeForDedup(schedule.startTime);
        const endTime = normalizeTimeForDedup(schedule.endTime);
        const locationId = getLocationIdForDedup(schedule);
        const key = `${userID}_scheduled_${startTime}_${endTime}_${locationId}`;
        
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }
    });

    return deduplicated;
  };

  // Calculate day hours from actual clock times ONLY (not scheduled times)
  const calculateDayHours = (schedules, date) => {
    let totalMilliseconds = 0;
    const now = new Date();
    
    // Check if this is today
    const scheduleDate = date ? new Date(date) : now;
    scheduleDate.setUTCHours(0, 0, 0, 0);
    const todayStart = new Date(now);
    todayStart.setUTCHours(0, 0, 0, 0);
    const isToday = scheduleDate.getTime() === todayStart.getTime();
    
    schedules.forEach((schedule) => {
      // ONLY use actual clock times - ignore scheduled times
      // For virtual schedules (from timesheets), use startTime/endTime as they are actual clock times
      const start = schedule.clockIn 
        ? new Date(schedule.clockIn) 
        : (schedule.isVirtual && schedule.startTime ? new Date(schedule.startTime) : null);
      
      // Determine end time:
      // 1. If clockOut exists, use it
      // 2. If no clockOut but it's today and shift is active, use current time (live calculation)
      // 3. If no clockOut and it's a past date, skip this schedule (don't count incomplete shifts)
      let end = null;
      if (schedule.clockOut) {
        end = new Date(schedule.clockOut);
      } else if (schedule.isVirtual && schedule.endTime) {
        // For virtual schedules (timesheets), use endTime if available
        end = new Date(schedule.endTime);
      } else if (schedule.clockIn && isToday) {
        // Only use current time for today's active shifts
        end = now;
      }
      // If no clockOut and it's a past date, end remains null (will skip this schedule)
      
      // Skip if no actual clock-in time or no end time (don't count scheduled-only or incomplete entries)
      if (!start || !end) return;
      
      const dayStart = new Date(start);
      dayStart.setUTCHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setUTCHours(23, 59, 59, 999);

      const overlapStart = start > dayStart ? start : dayStart;
      const overlapEnd = end < dayEnd ? end : dayEnd;
      let duration = overlapEnd - overlapStart;

      // Subtract actual break time if exists
      if (schedule.breakClockIn && schedule.breakClockOut) {
        const breakStart = new Date(schedule.breakClockIn);
        const breakEnd = new Date(schedule.breakClockOut);
        const breakOverlapStart = breakStart > dayStart ? breakStart : dayStart;
        const breakOverlapEnd = breakEnd < dayEnd ? breakEnd : dayEnd;
        if (breakOverlapStart < breakOverlapEnd) {
          duration -= (breakOverlapEnd - breakOverlapStart);
        }
      } else if (schedule.breakStartTime && schedule.breakEndTime) {
        // Fallback to scheduled break time
        const breakStart = new Date(schedule.breakStartTime);
        const breakEnd = new Date(schedule.breakEndTime);
        const breakOverlapStart = breakStart > dayStart ? breakStart : dayStart;
        const breakOverlapEnd = breakEnd < dayEnd ? breakEnd : dayEnd;
        if (breakOverlapStart < breakOverlapEnd) {
          duration -= (breakOverlapEnd - breakOverlapStart);
        }
      }
      
      if (duration > 0) {
        totalMilliseconds += duration;
      }
    });

    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const getDayWorkedDates = (userID) => {
    // Use merged schedules (includes timesheets) for accurate day counting
    const mergedSchedules = getMergedSchedulesForUser(userID);
    const dates = new Set();
    mergedSchedules.forEach((schedule) => {
      // Only count days with actual clock-in times (not just scheduled)
      // For virtual schedules (from timesheets), use startTime as it's the actual clock-in time
      const clockInDate = schedule.clockIn ? new Date(schedule.clockIn) : 
                         (schedule.isVirtual && schedule.startTime ? new Date(schedule.startTime) : null);
      
      if (clockInDate) {
        dates.add(clockInDate.toISOString().split("T")[0]);
      }
    });
    return dates.size;
  };

  // Calculate Issues count: Count days with issues for the user
  // A day has an issue (value 1) if:
  // - Time was manually edited by admin (editedFields exists)
  // - User didn't clock out (has startTime but no endTime)
  // - Clock in/out location is UNKNOWN LOCATION or null
  const calculateIssuesCount = (userID) => {
    // Get timesheets for this user within the date range
    const start = new Date(startDate);
    start.setUTCHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setUTCHours(23, 59, 59, 999);
    
    const userTimesheets = timesheets.filter((timesheet) => {
      if (timesheet.userID?._id !== userID && timesheet.userID !== userID) return false;
      if (!timesheet.startTime) return false;
      const tsStart = new Date(timesheet.startTime);
      return tsStart >= start && tsStart <= end;
    });

    // Track unique days with issues
    const daysWithIssues = new Set();

    userTimesheets.forEach((timesheet) => {
      if (!timesheet.startTime) return;
      
      // Get the date for this timesheet
      const tsDate = new Date(timesheet.startTime);
      tsDate.setUTCHours(0, 0, 0, 0);
      const dateKey = tsDate.toISOString().split("T")[0];
      
      // Check for issues
      let hasIssue = false;
      
      // Issue 1: Time was manually edited by admin
      if (timesheet.editedFields && (
        timesheet.editedFields.startTime ||
        timesheet.editedFields.endTime ||
        timesheet.editedFields.breakDuration ||
        timesheet.editedFields.notes
      )) {
        hasIssue = true;
      }
      
      // Issue 2: User didn't clock out (has startTime but no endTime)
      if (timesheet.startTime && !timesheet.endTime) {
        hasIssue = true;
      }
      
      // Issue 3: Clock in location is UNKNOWN LOCATION or null
      const clockInLocation = timesheet.clockInLocationID;
      const clockInLocationName = clockInLocation?.name || null;
      if (!clockInLocation || clockInLocationName === "UNKNOWN LOCATION" || clockInLocationName === null) {
        hasIssue = true;
      }
      
      // Issue 4: Clock out location is UNKNOWN LOCATION or null (if clocked out)
      if (timesheet.endTime) {
        const clockOutLocation = timesheet.clockOutLocationID;
        const clockOutLocationName = clockOutLocation?.name || null;
        if (!clockOutLocation || clockOutLocationName === "UNKNOWN LOCATION" || clockOutLocationName === null) {
          hasIssue = true;
        }
      }
      
      if (hasIssue) {
        daysWithIssues.add(dateKey);
      }
    });

    return daysWithIssues.size;
  };

  const handleExport = () => {
    setShowConfirmModal(true);
  };

  const confirmExport = () => {
    // Close modal and proceed with export
    setShowConfirmModal(false);
    performExport();
  };

  const performExport = () => {
    const exportData = [];
    users.forEach((user) => {
      if (selectedUsers.includes(user._id)) {
        // Use merged schedules (includes timesheets) for accurate calculations
        const mergedSchedules = getMergedSchedulesForUser(user._id);
        const days = getDayWorkedDates(user._id);
        const totalHours = calculateTotalHours(mergedSchedules);

        const row = {
          name: getUserFullName(user),
          days: days,
          hours: totalHours,
        };

        dateRange.forEach((date) => {
          const daySchedules = getSchedulesForDay(user._id, date);
          
          // Filter to only include schedules with actual clock-in times (not just scheduled)
          const clockedSchedules = daySchedules.filter((schedule) => {
            return schedule.clockIn || (schedule.isVirtual && schedule.startTime);
          });
          
          // Only calculate hours from clocked schedules
          const dayHours = calculateDayHours(clockedSchedules, date);
          let dayData = dayHours;
          
          if (clockedSchedules.length > 0) {
            // Sort schedules by clock-in time
            const sortedSchedules = [...clockedSchedules].sort((a, b) => {
              const timeA = a.clockIn ? new Date(a.clockIn) : (a.isVirtual && a.startTime ? new Date(a.startTime) : new Date(0));
              const timeB = b.clockIn ? new Date(b.clockIn) : (b.isVirtual && b.startTime ? new Date(b.startTime) : new Date(0));
              return timeA - timeB;
            });
            
            // Collect clock-in and clock-out pairs for each schedule
            const timePairs = [];
            
            sortedSchedules.forEach((schedule) => {
              // Get clock-in time
              const clockIn = schedule.clockIn 
                ? formatTime(schedule.clockIn) 
                : (schedule.isVirtual && schedule.startTime ? formatTime(schedule.startTime) : null);
              
              // Get clock-out time - only use if it actually exists and is different from clock-in
              let clockOut = null;
              
              if (schedule.clockOut) {
                // Use actual clockOut if available
                clockOut = formatTime(schedule.clockOut);
              } else if (schedule.isVirtual && schedule.endTime) {
                // For virtual schedules, only use endTime if it's different from startTime
                // (endTime might be set to startTime as fallback, which means no actual clock-out)
                const startTime = schedule.startTime ? new Date(schedule.startTime) : null;
                const endTime = schedule.endTime ? new Date(schedule.endTime) : null;
                
                if (startTime && endTime && endTime.getTime() !== startTime.getTime()) {
                  // endTime is different from startTime, so it's a real clock-out
                  clockOut = formatTime(schedule.endTime);
                }
              }
              
              // Create pair: "In: time1 Out: time2" or "In: time1 Out: -" if no clock-out
              if (clockIn) {
                const outTime = clockOut || "-";
                const pair = `In: ${clockIn} Out: ${outTime}`;
                timePairs.push(pair);
              }
            });
            
            // Format: "Total Hours | In: time1 Out: time2 | In: time1 Out: time2 | ..."
            const timeRange = timePairs.length > 0 ? timePairs.join(" | ") : "";
            
            dayData = {
              hours: dayHours,
              timeRange: timeRange,
            };
          }
          row[formatDate(date)] = dayData;
        });

        exportData.push(row);
      }
    });

    // Export as Excel (XLSX) format
    const headers = ["Name", "Days", "Hours", ...dateRange.map((d) => formatDate(d))];
    const data = exportData.map((row) => {
      return [
        row.name,
        row.days,
        row.hours,
        ...dateRange.map((date) => {
          const dayData = row[formatDate(date)];
          if (typeof dayData === "object") {
            // Format: "Total Hours | In: time1, time2 | Out: time1, time2"
            if (dayData.timeRange) {
              return `${dayData.hours} | ${dayData.timeRange}`;
            }
            return dayData.hours || "-";
          }
          return dayData || "-";
        }),
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WorkTime");

    const filename = `worktime-report-${format(startDate, "yyyy-MM-dd")}-${format(endDate, "yyyy-MM-dd")}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success("Excel file exported successfully");
  };

  const toggleUserSelection = (userID) => {
    setSelectedUsers((prev) =>
      prev.includes(userID)
        ? prev.filter((id) => id !== userID)
        : [...prev, userID]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u._id));
    }
  };

  const getUserFullName = (user) => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || user.preferredName || "Unknown";
  };

  const getUserInitials = (user) => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    const firstInitial = firstName.charAt(0).toUpperCase() || "";
    const lastInitial = lastName.charAt(0).toUpperCase() || "";
    return `${firstInitial}${lastInitial}` || "??";
  };

  const getFilteredUsers = () => {
    if (!staffSearchTerm) return users;
    return users.filter((user) => {
      const fullName = getUserFullName(user);
      return fullName.toLowerCase().includes(staffSearchTerm.toLowerCase());
    });
  };

  const getSelectedUsersNames = () => {
    if (selectedUsers.length === 0) return "Select staff";
    if (selectedUsers.length === users.length) return "All staff";
    const selected = users.filter((u) => selectedUsers.includes(u._id));
    if (selected.length === 1) return getUserFullName(selected[0]);
    return `${selected.length} staff selected`;
  };

  const handleDateChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Working":
        return "bg-green-500";
      case "Clocked Out":
        return "bg-blue-400";
      default:
        return "bg-[transparent]";
    }
  };

  const getDayStatus = (daySchedules, userID, date) => {
    const now = new Date();
    const scheduleDate = new Date(date);
    scheduleDate.setUTCHours(0, 0, 0, 0);
    const todayStart = new Date(now);
    todayStart.setUTCHours(0, 0, 0, 0);

    const isToday = scheduleDate.getTime() === todayStart.getTime();

    if (!isToday || daySchedules.length === 0) {
      return null;
    }

    // Check if any schedule has clock-in
    const hasClockIn = daySchedules.some(s => s.clockIn);
    
    // If no clock-in, don't show status
    if (!hasClockIn) {
      return null;
    }

    // Check if ALL schedules have clock-out (all shifts are complete)
    const allHaveClockOut = daySchedules.filter(s => s.clockIn).every(s => s.clockOut);
    
    // If all shifts are complete, show "Clocked Out"
    if (allHaveClockOut) {
      return "Clocked Out";
    }
    
    // If there are incomplete shifts, only show "Working" for the most recent one
    // Get the most recent clock-in that doesn't have a clock-out
    const incompleteShifts = daySchedules
      .filter(s => s.clockIn && !s.clockOut)
      .sort((a, b) => new Date(b.clockIn) - new Date(a.clockIn));
    
    // Only return "Working" if this is the most recent incomplete shift
    // We'll check this at the call site by comparing schedule clock-in times
    if (incompleteShifts.length > 0) {
      return "Working";
    }

    return null;
  };

  const shouldShowRedCircle = (entry) => {
    // Don't show red circle for virtual schedules (timesheet-only entries)
    if (entry.isVirtual) return false;
    
    // Don't show red circle if there's no scheduled end time
    if (!entry || !entry.endTime) return false;
    
    // Don't show red circle for entries that are already clocked out
    if (entry.clockOut) return false;
    
    // Only show red circle for real schedules that have passed their scheduled end time
    const endTime = new Date(entry.endTime);
    const now = new Date();
    return now > endTime;
  };

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">WorkTime Report</h1>
        <button
          onClick={handleExport}
          disabled={!!dateError}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            dateError
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gray-500 text-white hover:bg-gray-600"
          }`}
        >
          Export
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4 items-center flex-wrap">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
        {dateError && (
          <div className="text-red-600 text-sm font-medium">
            {dateError}
          </div>
        )}
        {/* Company Filter - Only for system administrators */}
        {user && user.deputyAccessLevel === "system_administrator" && (
          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-700">Company:</label>
            <div className="w-64">
              <Select
                name="company"
                placeholder="All Companies"
                className="basic-select w-full cursor-pointer"
                classNamePrefix="select"
                options={companies}
                value={selectedCompany}
                onChange={(option) => {
                  setSelectedCompany(option);
                }}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option._id}
                isClearable={true}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#9ca3af",
                    },
                  }),
                }}
              />
            </div>
          </div>
        )}
        <div className="flex gap-2 items-center ml-4">
          <label className="text-sm font-medium text-gray-700">Staff:</label>
          <div className="relative" ref={dropdownRef}>
            {/* Multi-select Dropdown Button */}
            <button
              onClick={() => {
                setIsStaffDropdownOpen(!isStaffDropdownOpen);
                setStaffSearchTerm("");
              }}
              className="w-64 px-3 py-2 bg-white border border-gray-300 rounded-md text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <span className="text-sm text-gray-700 truncate">
                {getSelectedUsersNames()}
              </span>
              <i className="fa fa-chevron-down text-gray-400 text-xs ml-2"></i>
            </button>

            {/* Dropdown Content */}
            {isStaffDropdownOpen && (
              <div className="absolute mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-80 overflow-hidden flex flex-col">
                {/* Search Input */}
                <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search"
                      value={staffSearchTerm}
                      onChange={(e) => setStaffSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                      autoFocus
                    />
                    <i className="fa fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                  </div>
                </div>

                {/* Staff Options List */}
                <div className="overflow-y-auto max-h-64">
                  {/* Select/Deselect All */}
                  <div
                    onClick={toggleAllUsers}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                        {selectedUsers.length === users.length ? (
                          <i className="fa fa-check text-xs text-gray-600"></i>
                        ) : (
                          ""
                        )}
                      </div>
                      <span className="text-sm text-gray-700 font-medium">
                        {selectedUsers.length === users.length ? "Deselect All" : "Select All"}
                      </span>
                    </div>
                  </div>

                  {/* Individual Staff Options */}
                  {getFilteredUsers().map((user) => (
                    <div
                      key={user._id}
                      onClick={() => toggleUserSelection(user._id)}
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                          {selectedUsers.includes(user._id) && (
                            <i className="fa fa-check text-xs text-gray-600"></i>
                          )}
                        </div>
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-2">
                          <span className="text-sm font-semibold text-gray-600">{getUserInitials(user)}</span>
                        </div>
                        <span className="text-sm text-gray-700">{getUserFullName(user)}</span>
                      </div>
                    </div>
                  ))}
                  
                  {getFilteredUsers().length === 0 && (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      No staff found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto overflow-y-auto rounded-lg" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200 sticky top-0">
              <th className="p-4 text-left font-bold text-gray-700">Employee Name</th>
              <th className="p-4 text-center font-bold text-gray-700">Days</th>
              <th className="p-4 text-center font-bold text-gray-700">Hours</th>
              <th className="p-4 text-center font-bold text-gray-700">Issues</th>
              {dateRange.map((date) => (
                <th key={date} className="p-4 text-center font-semibold text-gray-700 whitespace-nowrap">
                  {formatDate(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users
              .filter((user) => selectedUsers.includes(user._id))
              .map((user) => {
                // Use merged schedules (includes timesheets) for accurate calculations
                const mergedSchedules = getMergedSchedulesForUser(user._id);
                const days = getDayWorkedDates(user._id);
                const totalHours = calculateTotalHours(mergedSchedules);

                return (
                  <tr key={user._id} className="border border-gray-200 hover:bg-gray-50">
                    <td className="p-4 border border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-gray-600">{getUserInitials(user)}</span>
                        </div>
                        <span className="font-medium text-gray-900">{getUserFullName(user)}</span>
                      </div>
                    </td>
                    <td className="p-4 bg-[#f2fcfe] border border-gray-200 text-center text-gray-700">{days}</td>
                    <td className="p-4 bg-[#f2fcfe] border border-gray-200 text-center text-gray-700">{totalHours}</td>
                    <td className="p-4 bg-[#f2fcfe] border border-gray-200 text-center text-gray-700">
                      {calculateIssuesCount(user._id)}
                    </td>
                    {dateRange.map((date) => {
                      const daySchedules = getSchedulesForDay(user._id, date);
                      const dayHours = calculateDayHours(daySchedules, date);
                      const hasSchedules = daySchedules.length > 0;

                      if (!hasSchedules) {
                        return (
                          <td key={date} className="p-4 border border-gray-200 text-center text-gray-400">
                            -
                          </td>
                        );
                      }

                      return (
                        <td key={date} className="p-4 border border-gray-200">
                          <div className="text-center space-y-1">
                            {daySchedules.map((schedule, index) => {
                              const isVirtual = schedule.isVirtual === true;
                              
                              // Scheduled time and location - only show for non-virtual schedules
                              const scheduledStart = !isVirtual && schedule.startTime ? formatTime(schedule.startTime) : null;
                              const scheduledEnd = !isVirtual && schedule.endTime ? formatTime(schedule.endTime) : null;
                              const locationName = !isVirtual ? (schedule.locationID?.name || schedule.locationID?.area || "") : "";
                              
                              // Actual clock times - always show
                              const actualClockIn = schedule.clockIn ? formatTime(schedule.clockIn) : null;
                              const actualClockOut = schedule.clockOut ? formatTime(schedule.clockOut) : null;
                              
                              // Calculate work time for this specific entry
                              const entryStart = schedule.clockIn ? new Date(schedule.clockIn) : (schedule.startTime ? new Date(schedule.startTime) : null);
                              
                              // Determine end time:
                              // 1. If clockOut exists, use it
                              // 2. If no clockOut but it's today and shift is active, use current time (live calculation)
                              // 3. If no clockOut and it's a past date, don't calculate duration (show as incomplete)
                              const now = new Date();
                              const scheduleDate = new Date(date);
                              scheduleDate.setUTCHours(0, 0, 0, 0);
                              const todayStart = new Date(now);
                              todayStart.setUTCHours(0, 0, 0, 0);
                              const isToday = scheduleDate.getTime() === todayStart.getTime();
                              
                              let entryEnd = null;
                              if (schedule.clockOut) {
                                entryEnd = new Date(schedule.clockOut);
                              } else if (schedule.clockIn && isToday) {
                                // Only use current time for today's active shifts
                                entryEnd = now;
                              } else if (schedule.endTime && !schedule.clockIn) {
                                // For scheduled-only entries (no clock-in yet), use scheduled end time
                                entryEnd = new Date(schedule.endTime);
                              }
                              // If no clockOut and it's a past date, entryEnd remains null (won't calculate duration)
                              
                              let entryDuration = 0;
                              if (entryStart && entryEnd) {
                                entryDuration = entryEnd - entryStart;
                                
                                // Subtract actual break time if exists (breakClockIn/breakClockOut or breakStartTime/breakEndTime)
                                if (schedule.breakClockIn && schedule.breakClockOut) {
                                  const breakStart = new Date(schedule.breakClockIn);
                                  const breakEnd = new Date(schedule.breakClockOut);
                                  const breakDuration = breakEnd - breakStart;
                                  if (breakDuration > 0 && breakDuration < entryDuration) {
                                    entryDuration -= breakDuration;
                                  }
                                } else if (schedule.breakStartTime && schedule.breakEndTime) {
                                  const breakStart = new Date(schedule.breakStartTime);
                                  const breakEnd = new Date(schedule.breakEndTime);
                                  const breakDuration = breakEnd - breakStart;
                                  if (breakDuration > 0 && breakDuration < entryDuration) {
                                    entryDuration -= breakDuration;
                                  }
                                }
                                
                                // Apply automatic break deduction if user has it enabled
                                // Automatic break is a user setting that automatically deducts a fixed amount
                                // of break time from every shift, regardless of whether they took an actual break.
                                // 
                                // How it works:
                                // 1. If automaticBreak is enabled and automaticBreakMinutes is set (e.g., 20 minutes)
                                // 2. The system automatically subtracts those minutes from total work time
                                // 3. If shift duration < automatic break time, total work time = 0
                                // 4. Example: 3-minute shift with 20-minute auto break = 0 hours worked
                                //
                                // Get user's automatic break settings from the current user context
                                const currentUser = users.find(u => u._id === user._id);
                                if (currentUser && currentUser.automaticBreak && currentUser.automaticBreakMinutes) {
                                  // Convert automatic break minutes to milliseconds
                                  const automaticBreakMs = currentUser.automaticBreakMinutes * 60 * 1000;
                                  
                                  // Subtract automatic break time
                                  // If the shift duration is less than automatic break, result is 0
                                  if (entryDuration >= automaticBreakMs) {
                                    entryDuration -= automaticBreakMs;
                                  } else {
                                    // If shift is shorter than automatic break, work time is 0
                                    // This matches the behavior shown in the example where a 3-minute shift
                                    // with 20-minute auto break results in 0 hours worked
                                    entryDuration = 0;
                                  }
                                }
                              }
                              
                              const totalSeconds = Math.floor(entryDuration / 1000);
                              const hours = Math.floor(totalSeconds / 3600);
                              const minutes = Math.floor((totalSeconds % 3600) / 60);
                              const seconds = totalSeconds % 60;
                              const entryWorkTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
                              
                              const showRedCircle = shouldShowRedCircle(schedule);
                              
                              // Determine status: Only show "Working" for the most recent incomplete shift
                              // Get all incomplete shifts for this day, sorted by clock-in time (most recent first)
                              const incompleteShifts = daySchedules
                                .filter(s => s.clockIn && !s.clockOut)
                                .sort((a, b) => new Date(b.clockIn) - new Date(a.clockIn));
                              
                              // Only show "Working" status if this is the most recent incomplete shift
                              let entryStatus = null;
                              if (schedule.clockIn && !schedule.clockOut) {
                                // Check if this is the most recent incomplete shift
                                // Compare by converting to timestamps to handle Date objects/strings properly
                                const scheduleClockInTime = new Date(schedule.clockIn).getTime();
                                const mostRecentClockInTime = incompleteShifts.length > 0 
                                  ? new Date(incompleteShifts[0].clockIn).getTime() 
                                  : null;
                                
                                const isMostRecent = mostRecentClockInTime !== null && 
                                  scheduleClockInTime === mostRecentClockInTime;
                                
                                if (isMostRecent) {
                                  // Only show status for today
                                  const now = new Date();
                                  const scheduleDate = new Date(date);
                                  scheduleDate.setUTCHours(0, 0, 0, 0);
                                  const todayStart = new Date(now);
                                  todayStart.setUTCHours(0, 0, 0, 0);
                                  const isToday = scheduleDate.getTime() === todayStart.getTime();
                                  
                                  if (isToday) {
                                    const status = realTimeStatus[user._id]?.status;
                                    entryStatus = status || "Working";
                                  }
                                }
                              } else if (schedule.clockIn && schedule.clockOut) {
                                // For completed shifts, check if all shifts on this day are complete
                                const allComplete = daySchedules
                                  .filter(s => s.clockIn)
                                  .every(s => s.clockOut);
                                
                                if (allComplete) {
                                  // Only show "Clocked Out" for today
                                  const now = new Date();
                                  const scheduleDate = new Date(date);
                                  scheduleDate.setUTCHours(0, 0, 0, 0);
                                  const todayStart = new Date(now);
                                  todayStart.setUTCHours(0, 0, 0, 0);
                                  const isToday = scheduleDate.getTime() === todayStart.getTime();
                                  
                                  if (isToday) {
                                    entryStatus = "Clocked Out";
                                  }
                                }
                              }

                              return (
                                <React.Fragment key={schedule._id || index}>
                                  {index > 0 && <hr className="border-gray-300 my-2" />}
                                  
                                  {/* Scheduled time and location - only for non-virtual schedules */}
                                  {scheduledStart && scheduledEnd && (
                                    <div className="text-sm text-[#547ecd] font-medium">
                                      {scheduledStart}-{scheduledEnd}
                                    </div>
                                  )}
                                  {locationName && (
                                    <div className="text-xs text-[#547ecd]">
                                      {locationName}
                                    </div>
                                  )}
                                  
                                  {/* Separator line between scheduled and actual times */}
                                  {(scheduledStart || locationName) && (actualClockIn || actualClockOut) && (
                                    <hr className="border-gray-300 my-1" />
                                  )}
                                   
                                  {/* Actual clock in/out times - always show if available */}
                                  {actualClockIn && (
                                    <div className="text-xs text-gray-600 mt-1">
                                      IN: {actualClockIn}
                                      {actualClockOut && ` OUT: ${actualClockOut}`}
                                    </div>
                                  )}
                                  
                                  {/* Total work time - only show if we have both start and end, or if it's today and active */}
                                  {actualClockIn && entryDuration > 0 && (
                                    <div className="text-sm font-medium text-gray-900 mt-1">
                                      {entryWorkTime}
                                    </div>
                                  )}
                                  {/* Show warning if clocked in but no clock out on past dates */}
                                  {actualClockIn && !actualClockOut && !isToday && (
                                    <div className="text-xs text-orange-600 mt-1 italic">
                                      Incomplete shift
                                    </div>
                                  )}
                                  
                                  {/* Status indicators */}
                                  {entryStatus && (
                                    <div className="flex items-center justify-center mt-1">
                                      <span className={`w-3 h-3 rounded-full ${getStatusColor(entryStatus)}`}></span>
                                      <span className="ml-1 text-xs text-gray-600">{entryStatus}</span>
                                    </div>
                                  )}
                                  {showRedCircle && (
                                    <div className="w-3 h-3 bg-red-500 rounded-full mx-auto mt-1"></div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Confirm Export Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-700">Confirm Export</h2>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-base text-gray-700">
                Are you sure you want to export the worktime report?
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmExport}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Step 1: Calculate total shift duration
//    = Clock Out Time - Clock In Time
//    (or current time if still clocked in)

// Step 2: Subtract actual break time (if exists)
//    - If breakClockIn/breakClockOut exists, subtract that duration
//    - OR if breakStartTime/breakEndTime exists, subtract that duration

// Step 3: Apply automatic break deduction (if enabled)
//    - If user.automaticBreak = true AND user.automaticBreakMinutes > 0
//    - Subtract automaticBreakMinutes from remaining duration
//    - If remaining duration < automaticBreakMinutes, set to 0

// Step 4: Calculate final work time
//    = (Total Duration - Actual Breaks - Automatic Break)

